import React, { useState, useEffect } from 'react';
import { useParams, useLocation } from 'react-router-dom';
import { customBaseUrl, httpGet, httpPost, httpGetPdf, httpGetBlob, httpPatch } from '../../store/http';
import { saveAs } from 'file-saver';
import { NotificationManager } from 'react-notifications';
import {
    Card,
    Button,
    Space,
    Modal,
    Table,
    Typography,
    Row,
    Col,
    Tag,
    Divider,
    Avatar,
    Descriptions,
    Alert,
    Input,
    Tabs,
    Checkbox,
    Select,
    Form
} from 'antd';
import {
    FileTextOutlined,
    UserOutlined,
    HistoryOutlined,
    DollarOutlined,
    BankOutlined,
    SafetyCertificateOutlined,
    DownloadOutlined
} from '@ant-design/icons';
import LoanDashboardLayout from '../../components/Layout/LoanDashboardLayout';
import LoanHistoryModal from '../../components/Modal/LoanHistoryModal';
import ApprovalHistoryModal from '../../components/Modal/ApprovalHistoryModal';
import moment from 'moment';
import AdminLayout from "../../components/Layout";
import { hideLoader, showLoader } from '../../utils/loader';
import { useMatStyles, CardWrapper } from './style';

const { Title, Text } = Typography;
const { TabPane } = Tabs;
const { Search } = Input;

const LoanViewPage = (props) => {
    const { id } = useParams();
    const location = useLocation();
    const classes = useMatStyles();
    const from = props.from || location.state?.from;
    const [loanDetails, setLoanDetails] = useState(null);
    const [loading, setLoading] = useState(true);
    const [isOtherDataModalVisible, setIsOtherDataModalVisible] = useState(false);
    const [isMandateModalVisible, setIsMandateModalVisible] = useState(false);
    const [mandateDetails, setMandateDetails] = useState(null);
    const [selectedLoanIdForHistory, setSelectedLoanIdForHistory] = useState(null);
    const [isLoanHistoryModalVisible, setIsLoanHistoryModalVisible] = useState(false);
    const [approvalHistory, setApprovalHistory] = useState(null);
    const [isApprovalHistoryModalVisible, setIsApprovalHistoryModalVisible] = useState(false);
    const [isGuarantorDetailsModalVisible, setIsGuarantorDetailsModalVisible] = useState(false);
    const [selectedGuarantorDetails, setSelectedGuarantorDetails] = useState(null);
    const [refresh, setRefresh] = useState(false);

    // OTP Verification States
    const [isVerifyOtpModalVisible, setIsVerifyOtpModalVisible] = useState(false);
    const [currentGuarantorAuthUserId, setCurrentGuarantorAuthUserId] = useState(null);
    const [otpInput, setOtpInput] = useState('');
    const [verifyingOtp, setVerifyingOtp] = useState(false);
    const [resendingOtp, setResendingOtp] = useState(false);
    const [activeTab, setActiveTab] = useState('overview');
    const [repayments, setRepayments] = useState([]);
    const [repaymentFilter, setRepaymentFilter] = useState('');
    const [fetchingRepayments, setFetchingRepayments] = useState(false);
    const [isReconcileModalVisible, setIsReconcileModalVisible] = useState(false);
    const [reconcileRef, setReconcileRef] = useState('');
    const [isDryRun, setIsDryRun] = useState(true);
    const [reconcileResult, setReconcileResult] = useState(null);
    const [reconciling, setReconciling] = useState(false);

    const [isUpdateCycleModalVisible, setIsUpdateCycleModalVisible] = useState(false);
    const [updatingCycle, setUpdatingCycle] = useState(false);
    const [repaymentCycleValue, setRepaymentCycleValue] = useState(30);
    const [repaymentCycleUnit, setRepaymentCycleUnit] = useState('days');
    const [firstRepaymentDelayValue, setFirstRepaymentDelayValue] = useState('');
    const [firstRepaymentDelayUnit, setFirstRepaymentDelayUnit] = useState(undefined);

    const getRoles = () => {
        const rawRoles = localStorage.getItem('roles');
        if (!rawRoles) return [];
        try {
            const parsed = JSON.parse(rawRoles);
            return Array.isArray(parsed) ? parsed : [rawRoles];
        } catch (e) {
            return rawRoles.split(',').map(r => r.trim());
        }
    };
    const roles = getRoles();
    const canValidateGuarantor = roles?.includes('loan_admin') || roles?.includes('OWNER') || roles?.includes('SUPER_ADMIN');
    const isFinanceApprover = roles?.includes('finance_approver') || roles?.includes('OWNER') || roles?.includes('SUPER_ADMIN');

    useEffect(() => {
        const fetchLoanDetails = async () => {
            try {
                const token = from === 'admin' ? localStorage.getItem('token') : localStorage.getItem('loanAdminToken');
                if (!token) {
                    window.location.href = '/loan-admin-login';
                    return;
                }
                showLoader();
                const res = await httpGet(
                    `${customBaseUrl.loanUrl}/api/v1/admin/loans/${id}`,
                    null,
                    false,
                    false,
                    token
                );
                if (res.status) {
                    setLoanDetails(res.data);
                } else {
                    NotificationManager.error(res.message || 'Failed to fetch loan details');
                }
            } catch (error) {
                console.error('Error fetching loan details:', error);
                NotificationManager.error('Error fetching loan details');
            } finally {
                setLoading(false);
                hideLoader();
            }
        };

        fetchLoanDetails();
    }, [id, from, refresh]);

    useEffect(() => {
        const fetchRepayments = async () => {
            if (activeTab !== 'repayments') return;
            setFetchingRepayments(true);
            try {
                const token = from === 'admin' ? localStorage.getItem('token') : localStorage.getItem('loanAdminToken');
                const query = repaymentFilter ? `?reference=${repaymentFilter}` : '';
                const res = await httpGet(
                    `${customBaseUrl.loanUrl}/api/v1/admin/loans/${id}/repayments${query}`,
                    null,
                    false,
                    false,
                    token
                );
                if (res.status) {
                    setRepayments(res.data);
                } else {
                    NotificationManager.error(res.message || 'Failed to fetch repayments');
                }
            } catch (error) {
                console.error('Error fetching repayments:', error);
                NotificationManager.error('Error fetching repayments');
            } finally {
                setFetchingRepayments(false);
            }
        };

        fetchRepayments();
    }, [id, from, activeTab, repaymentFilter, refresh]);

    const handleManualReconcile = async () => {
        if (!reconcileRef) {
            NotificationManager.warning('Please enter a transaction reference');
            return;
        }
        setReconciling(true);
        setReconcileResult(null);
        try {
            const token = from === 'admin' ? localStorage.getItem('token') : localStorage.getItem('loanAdminToken');
            const res = await httpPost(
                `${customBaseUrl.loanUrl}/api/v1/admin/reconcile-payment-by-reference`,
                { reference: reconcileRef, dry_run: isDryRun },
                false,
                token
            );
            if (res.status) {
                setReconcileResult(res);
                NotificationManager.success(res.message || 'Reconciliation successful');
                if (!isDryRun) {
                    setRepaymentFilter(''); // Reset filter to show all
                    // Repayments will be re-fetched by the useEffect due to activeTab remaining 'repayments'
                }
            } else {
                NotificationManager.error(res.message || 'Reconciliation failed');
            }
        } catch (error) {
            console.error('Reconciliation error:', error);
            NotificationManager.error('Error during reconciliation');
        } finally {
            setReconciling(false);
        }
    };

    const handleExportDetails = async () => {
        showLoader();
        try {
            const token = from === 'admin' ? localStorage.getItem('token') : localStorage.getItem('loanAdminToken');
            const blob = await httpGetBlob(
                `${customBaseUrl.loanUrl}/api/v1/admin/actions/${id}/export-details`,
                token
            );
            if (blob) {
                saveAs(blob, `Loan_Participant_Details_${id}_${moment().format('YYYYMMDD_HHmmss')}.xlsx`);
                NotificationManager.success('Details exported successfully', 'Success');
            } else {
                NotificationManager.error('Failed to export details', 'Error');
            }
        } catch (error) {
            console.error('Export error:', error);
            NotificationManager.error('An error occurred during export', 'Error');
        } finally {
            hideLoader();
        }
    };

    const handleVerifyGuarantor = async (accountNumber) => {
        if (!accountNumber) {
            NotificationManager.warning("No account number found for this guarantor.");
            return;
        }
        showLoader();
        try {
            const token = from === 'admin' ? localStorage.getItem('token') : localStorage.getItem('loanAdminToken');
            const res = await httpGet(
                `${customBaseUrl.loanUrl}/api/v1/admin/users/account/${accountNumber}`,
                null,
                false,
                false,
                token
            );
            if (res.status && res.data) {
                setSelectedGuarantorDetails(res.data);
                setIsGuarantorDetailsModalVisible(true);
            } else {
                NotificationManager.error(res.message || "Failed to fetch guarantor details.");
            }
        } catch (error) {
            NotificationManager.error("Error fetching guarantor details.");
        } finally {
            hideLoader();
        }
    };

    const handleUpdateRepaymentCycle = async () => {
        if (!repaymentCycleValue || repaymentCycleValue < 1) {
            NotificationManager.error('Repayment cycle value must be a positive integer');
            return;
        }
        if (!repaymentCycleUnit) {
            NotificationManager.error('Repayment cycle unit is required');
            return;
        }

        setUpdatingCycle(true);
        showLoader();
        try {
            const token = from === 'admin' ? localStorage.getItem('token') : localStorage.getItem('loanAdminToken');
            const payload = {
                repayment_cycle_value: Number(repaymentCycleValue),
                repayment_cycle_unit: repaymentCycleUnit,
            };
            if (firstRepaymentDelayValue !== '') {
                payload.first_repayment_delay_value = Number(firstRepaymentDelayValue);
            }
            if (firstRepaymentDelayUnit) {
                payload.first_repayment_delay_unit = firstRepaymentDelayUnit;
            }

            const res = await httpPatch(
                `${customBaseUrl.loanUrl}/api/v1/admin/loans/${id}/repayment-cycle`,
                payload,
                null,
                false,
                token
            );

            if (res.status) {
                NotificationManager.success(res.message || 'Repayment cycle updated successfully');
                setIsUpdateCycleModalVisible(false);
                setRefresh(prev => !prev);
            } else {
                NotificationManager.error(res.message || 'Failed to update repayment cycle');
            }
        } catch (error) {
            NotificationManager.error(error.message || 'An error occurred during repayment cycle update');
        } finally {
            setUpdatingCycle(false);
            hideLoader();
        }
    };

    const handleReverseRepayment = async (repaymentId) => {
        Modal.confirm({
            title: 'Confirm Reversal',
            content: 'Are you sure you want to reverse this loan repayment? This action cannot be undone.',
            okText: 'Yes, Reverse',
            okType: 'danger',
            cancelText: 'No, Cancel',
            onOk: async () => {
                showLoader();
                try {
                    const token = from === 'admin' ? localStorage.getItem('token') : localStorage.getItem('loanAdminToken');
                    const res = await httpPatch(
                        `${customBaseUrl.loanUrl}/api/v1/admin/repayments/${repaymentId}/reverse`,
                        {},
                        null,
                        false,
                        token
                    );
                    if (res.status) {
                        NotificationManager.success(res.message || 'Repayment reversed successfully');
                        setRefresh(prev => !prev);
                    } else {
                        NotificationManager.error(res.message || 'Failed to reverse repayment');
                    }
                } catch (error) {
                    NotificationManager.error(error.message || 'An error occurred during repayment reversal');
                } finally {
                    hideLoader();
                }
            }
        });
    };

    const handleResendGuarantorOtp = async (guarantorAuthUserId) => {
        setResendingOtp(true);
        try {
            const token = from === 'admin' ? localStorage.getItem('token') : localStorage.getItem('loanAdminToken');
            const res = await httpPost(
                `${customBaseUrl.loanUrl}/api/v1/loans/${id}/resend-guarantor-otp`,
                { guarantor_auth_user_id: guarantorAuthUserId },
                false,
                token
            );
            if (res.status) {
                NotificationManager.success(res.message || "OTP resent successfully!");
            } else {
                NotificationManager.error(res.message || "Failed to resend OTP.");
            }
        } catch (error) {
            NotificationManager.error("Error resending OTP.");
        } finally {
            setResendingOtp(false);
        }
    };

    const handleVerifyGuarantorOtp = async () => {
        if (!otpInput) {
            NotificationManager.warning("Please enter the OTP.");
            return;
        }
        setVerifyingOtp(true);
        try {
            const token = from === 'admin' ? localStorage.getItem('token') : localStorage.getItem('loanAdminToken');
            const res = await httpPost(
                `${customBaseUrl.loanUrl}/api/v1/loans/${id}/verify-otp`,
                {
                    otp: otpInput,
                    guarantor_auth_user_id: currentGuarantorAuthUserId
                },
                false,
                token
            );
            if (res.status) {
                NotificationManager.success(res.message || "Guarantor OTP verified successfully!");
                setIsVerifyOtpModalVisible(false);
                setOtpInput('');
                setRefresh(prev => !prev);
            } else {
                NotificationManager.error(res.message || "Invalid OTP.");
            }
        } catch (error) {
            NotificationManager.error("Error verifying OTP.");
        } finally {
            setVerifyingOtp(false);
        }
    };

    const handlePreviewOfferLetter = async () => {
        try {
            showLoader();
            const token = from === 'admin' ? localStorage.getItem('token') : localStorage.getItem('loanAdminToken');
            const res = await httpGetPdf(
                `${customBaseUrl.loanUrl}/api/v1/loans/${id}/preview-offer-letter`,
                null,
                false,
                false,
                token
            );

            const blob = new Blob([res], { type: 'application/pdf' });
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `loan-offer-preview-${id}.pdf`;
            document.body.appendChild(a);
            a.click();
            window.URL.revokeObjectURL(url);
            a.remove();
        } catch (error) {
            NotificationManager.error('Error fetching offer letter preview');
        } finally {
            hideLoader();
        }
    };

    const handleViewMandate = async () => {
        showLoader();
        try {
            const token = from === 'admin' ? localStorage.getItem('token') : localStorage.getItem('loanAdminToken');
            const res = await httpGet(
                `${customBaseUrl.loanUrl}/api/v1/admin/mandates/${loanDetails.mandate_id}`,
                null,
                false,
                false,
                token
            );
            if (res.status) {
                setMandateDetails(res.data);
                setIsMandateModalVisible(true);
            }
        } finally {
            hideLoader();
        }
    };

    const handleViewApprovalHistory = async () => {
        showLoader();
        try {
            const token = from === 'admin' ? localStorage.getItem('token') : localStorage.getItem('loanAdminToken');
            const res = await httpGet(
                `${customBaseUrl.loanUrl}/api/v1/loans/${id}/history`,
                null,
                false,
                false,
                token
            );
            if (res) {
                setApprovalHistory(res);
                setIsApprovalHistoryModalVisible(true);
            }
        } finally {
            hideLoader();
        }
    };

    const Layout = from === "admin" ? AdminLayout : LoanDashboardLayout;

    if (loading || !loanDetails) {
        return <Layout>{loading ? 'Loading...' : 'Loan not found.'}</Layout>;
    }

    const guarantorCount = (loanDetails.guarantors?.length > 0 ? loanDetails.guarantors : loanDetails.guarantor_details)?.length || 0;

    const InfoRow = ({ label, value, color }) => (
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16 }}>
            <Text type="secondary" style={{ fontSize: 13 }}>{label}</Text>
            <Text strong style={{ color: color || '#1a1a1a' }}>{value}</Text>
        </div>
    );

    return (
        <Layout>
            <div style={{ padding: '0 32px 32px 32px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 32 }}>
                    <div>
                        <Title level={2} style={{ margin: 0, fontWeight: 800 }}>Loan Request Preview</Title>
                        <Space>
                            <Tag color="blue" style={{ borderRadius: 6 }}>ID: {loanDetails.id}</Tag>
                            <Tag color={loanDetails.status === 'Approved' ? 'green' : 'orange'} style={{ borderRadius: 6 }}>{loanDetails.status}</Tag>
                        </Space>
                    </div>
                    <Space>

                        {loanDetails.loan_admin_approval_status === "Approved" &&
                            loanDetails.loan_officer_approval_status === "Approved" &&
                            loanDetails.finance_approver_approval_status === "Approved" && (
                                <Button
                                    icon={<FileTextOutlined />}
                                    style={{ height: 44, borderRadius: 10, background: '#0086E8', color: '#fff', border: 'none' }}
                                    onClick={handlePreviewOfferLetter}
                                >
                                    Preview Offer Letter
                                </Button>
                            )}
                        {(loanDetails.offer_letter_document) && (
                            <Button
                                icon={<FileTextOutlined />}
                                style={{ height: 44, borderRadius: 10, background: '#52c41a', color: '#fff', border: 'none' }}
                                onClick={() => window.open(loanDetails.offer_letter_document || loanDetails.offer_url, '_blank')}
                            >
                                View Signed Offer Letter
                            </Button>
                        )}
                    </Space>
                </div>

                <Tabs activeKey={activeTab} onChange={setActiveTab} style={{ background: '#fff', padding: '20px', borderRadius: '16px', boxShadow: '0 4px 12px rgba(0,0,0,0.05)' }}>
                    <TabPane tab="Overview" key="overview">
                        <Row gutter={[24, 24]}>
                            <Col xs={24} lg={16}>
                                <Space direction="vertical" size={24} style={{ width: '100%' }}>
                                    {/* User Summary Card */}
                                    <CardWrapper>
                                        <Card style={{ borderRadius: 16 }}>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: 20, marginBottom: 24 }}>
                                                <Avatar size={64} src={loanDetails.user?.other_data?.profilePictureUrl} icon={<UserOutlined />} style={{ backgroundColor: '#FF4B01' }} />
                                                <div>
                                                    <Title level={4} style={{ margin: 0 }}>{loanDetails.user?.other_data?.fullName || `${loanDetails.user?.first_name} ${loanDetails.user?.last_name}`}</Title>
                                                    <Text type="secondary">{loanDetails.user?.email} • {loanDetails.user?.phone}</Text>
                                                </div>
                                            </div>
                                            <Divider style={{ margin: '16px 0' }} />
                                            <Row gutter={40}>
                                                <Col span={12}>
                                                    <InfoRow label="Tier" value={loanDetails.user?.other_data?.tierDisplayName || `Level ${loanDetails.user?.kyc_level}`} />
                                                    <InfoRow label="KYC Status" value={loanDetails.user?.other_data?.kycStatus} />
                                                    <InfoRow label="BVN" value={loanDetails.user?.bvn} />
                                                    <InfoRow label="Gender" value={loanDetails.user?.other_data?.gender} />
                                                </Col>
                                                <Col span={12}>
                                                    <InfoRow label="Location" value={`${loanDetails.user?.other_data?.city}, ${loanDetails.user?.other_data?.state}`} />
                                                    <InfoRow label="User Type" value={loanDetails.user?.other_data?.userType} />
                                                    <InfoRow label="Account Status" value={<Tag color={loanDetails.user?.other_data?.status === 'ACTIVE' ? 'success' : 'error'}>{loanDetails.user?.other_data?.status}</Tag>} />
                                                    <Button type="link" style={{ padding: 0 }} onClick={() => setIsOtherDataModalVisible(true)}>View Full Profile</Button>
                                                </Col>
                                            </Row>
                                        </Card>
                                    </CardWrapper>

                                    {/* Installments Table */}
                                    {loanDetails.installment_dates?.length > 0 && (
                                        <Card title={<Space><DollarOutlined /> Payment Schedule</Space>} style={{ borderRadius: 16 }}>
                                            <Table
                                                dataSource={loanDetails.installment_dates.map((item, index) => ({
                                                    key: index,
                                                    date: moment(item.date).format('YYYY-MM-DD HH:mm:ss'),
                                                    amount: `₦${Number(item.amount).toLocaleString()}`,
                                                }))}
                                                columns={[
                                                    { title: 'Payment Date', dataIndex: 'date', key: 'date' },
                                                    { title: 'Amount', dataIndex: 'amount', key: 'amount', align: 'right' },
                                                ]}
                                                pagination={false}
                                                size="small"
                                            />
                                            {isFinanceApprover && (loanDetails.status === 'Disbursed' || loanDetails.status === 'Completed') && (
                                                <div style={{ marginTop: 16, textAlign: 'right' }}>
                                                    <Button
                                                        type="primary"
                                                        onClick={() => {
                                                            setRepaymentCycleValue(loanDetails.repayment_cycle_value || 30);
                                                            setRepaymentCycleUnit(loanDetails.repayment_cycle_unit || 'days');
                                                            setFirstRepaymentDelayValue(loanDetails.first_repayment_delay_value || '');
                                                            setFirstRepaymentDelayUnit(loanDetails.first_repayment_delay_unit || undefined);
                                                            setIsUpdateCycleModalVisible(true);
                                                        }}
                                                    >
                                                        Update Repayment Cycle
                                                    </Button>
                                                </div>
                                            )}
                                        </Card>
                                    )}

                                    {/* Employment & Bank Details */}
                                    <Row gutter={24}>
                                        <Col span={12}>
                                            <Card title={<Space><FileTextOutlined /> Employment</Space>} style={{ borderRadius: 16, height: '100%' }}>
                                                <InfoRow label="Employer" value={loanDetails.employment_details?.employer_name} />
                                                <InfoRow label="Company" value={loanDetails.employment_details?.company_name} />
                                                <InfoRow label="Company Address" value={loanDetails.employment_details?.company_address} />
                                                <InfoRow label="Salary" value={`₦${loanDetails.employment_details?.salary?.toLocaleString()}`} />
                                                <InfoRow label="Occupation" value={loanDetails.employment_details?.occupation_type} />
                                                <Divider style={{ margin: '12px 0' }} />
                                                <InfoRow label="HR Name" value={loanDetails.employment_details?.hr_name || 'N/A'} />
                                                <InfoRow label="HR Email" value={loanDetails.employment_details?.hr_email || 'N/A'} />
                                                <InfoRow label="HR Phone" value={loanDetails.employment_details?.hr_phone || 'N/A'} />
                                            </Card>
                                        </Col>
                                        <Col span={12}>
                                            <Card title={<Space><BankOutlined /> Bank Information</Space>} style={{ borderRadius: 16, height: '100%' }}>
                                                {loanDetails.repayment_details ? (
                                                    <div>
                                                        <InfoRow label="Bank" value={loanDetails.repayment_details.institution?.name} />
                                                        <InfoRow label="A/C Name" value={loanDetails.repayment_details.account_name} />
                                                        <InfoRow label="A/C No" value={loanDetails.repayment_details.account_number} />
                                                        <InfoRow label="A/C Type" value={loanDetails.repayment_details.account_type} />
                                                    </div>
                                                ) : loanDetails.account_details?.map((acc, i) => (
                                                    <div key={i} style={{ marginBottom: i === 0 ? 12 : 0 }}>
                                                        <InfoRow label="Bank" value={acc?.institution?.name} />
                                                        <InfoRow label="A/C No" value={acc?.account_number} />
                                                    </div>
                                                ))}
                                            </Card>
                                        </Col>
                                    </Row>

                                    {(loanDetails.guarantors?.length > 0 ? loanDetails.guarantors : loanDetails.guarantor_details)?.length > 0 && (
                                        <Card title={<Space><UserOutlined /> Guarantor Information</Space>} style={{ borderRadius: 16 }}>
                                            {(loanDetails.guarantors?.length > 0 ? loanDetails.guarantors : loanDetails.guarantor_details).map((guarantor, index) => {
                                                const richData = guarantor.other_data || {};
                                                const authUserId = richData.id || guarantor.auth_user_id;
                                                const otpStatus = loanDetails.guarantor_details?.find(g => g.auth_user_id == authUserId)?.guarantor_otp_status || guarantor.guarantor_otp_status || 'Pending';
                                                const accountNumber = richData.primaryAccountNumber || guarantor.account_number;

                                                return (
                                                    <div key={index} style={{ marginBottom: index === (loanDetails.guarantors?.length || loanDetails.guarantor_details?.length) - 1 ? 0 : 24 }}>
                                                        <Row gutter={24} align="middle">
                                                            <Col span={2}>
                                                                <Avatar size={48} src={richData.profilePictureUrl} icon={<UserOutlined />} />
                                                            </Col>
                                                            <Col span={8}>
                                                                <InfoRow label="Full Name" value={richData.fullName || guarantor.name || `${guarantor.first_name} ${guarantor.last_name}`} />
                                                                <InfoRow label="WAYA Account" value={accountNumber} />
                                                            </Col>
                                                            <Col span={8}>
                                                                <InfoRow label="Tier" value={richData.tierDisplayName || `Lvl ${guarantor.kyc_level || 'N/A'}`} />
                                                                <InfoRow label="OTP Status" value={<Tag color={otpStatus === 'Approved' ? 'success' : otpStatus === 'Pending' ? 'orange' : 'error'}>{otpStatus}</Tag>} />
                                                            </Col>
                                                            <Col span={6} style={{ textAlign: 'right' }}>
                                                                <Space split={<Divider type="vertical" />}>
                                                                    <Button
                                                                        type="primary"
                                                                        size="small"
                                                                        ghost
                                                                        onClick={() => handleVerifyGuarantor(accountNumber)}
                                                                    >
                                                                        Details
                                                                    </Button>
                                                                    {canValidateGuarantor && otpStatus !== 'Verified' && otpStatus !== 'Approved' && (
                                                                        <Button
                                                                            type="primary"
                                                                            size="small"
                                                                            onClick={() => {
                                                                                setCurrentGuarantorAuthUserId(authUserId);
                                                                                setIsVerifyOtpModalVisible(true);
                                                                            }}
                                                                        >
                                                                            Verify OTP
                                                                        </Button>
                                                                    )}
                                                                </Space>
                                                            </Col>
                                                        </Row>
                                                        {index < (loanDetails.guarantors?.length || loanDetails.guarantor_details?.length) - 1 && <Divider style={{ margin: '12px 0' }} />}
                                                    </div>
                                                );
                                            })}
                                        </Card>
                                    )}
                                </Space>
                            </Col>

                            <Col xs={24} lg={8}>
                                <Space direction="vertical" size={24} style={{ width: '100%' }}>
                                    <Card title="Loan Summary" style={{ borderRadius: 16 }}>
                                        <div style={{ textAlign: 'center', marginBottom: 24, padding: '16px', background: '#f8f9fa', borderRadius: 12 }}>
                                            <Text type="secondary" block>Amount Requested</Text>
                                            <Title level={2} style={{ margin: 0, color: '#FF4B01' }}>₦{loanDetails.amount?.toLocaleString()}</Title>
                                        </div>
                                        <InfoRow label="Type" value={loanDetails.type} />
                                        <InfoRow label="Term" value={`${loanDetails.term_months} Months`} color="#0086E8" />
                                        <InfoRow label="Needed By" value={loanDetails.needed_by} />
                                        <InfoRow label="Created" value={moment(loanDetails.created_at).format('MMM Do YYYY')} />
                                    </Card>

                                    <Card title="Loan Information" style={{ borderRadius: 16 }}>
                                        <Space direction="vertical" style={{ width: '100%' }}>
                                            <Button block icon={<HistoryOutlined />} onClick={handleViewApprovalHistory}>Approval History</Button>
                                            <Button block icon={<SafetyCertificateOutlined />} onClick={handleViewMandate}>View Mandate Details</Button>
                                            <Button block icon={<FileTextOutlined />} onClick={() => { setSelectedLoanIdForHistory(id); setIsLoanHistoryModalVisible(true); }}>Full Loan History</Button>
                                            <Button block icon={<DownloadOutlined />} onClick={handleExportDetails}>Download Profile Details (Excel)</Button>
                                            {(loanDetails.offer_letter_document || loanDetails.offer_url) && (
                                                <Button
                                                    block
                                                    type="primary"
                                                    style={{ background: '#52c41a', borderColor: '#52c41a' }}
                                                    icon={<FileTextOutlined />}
                                                    onClick={() => window.open(loanDetails.offer_letter_document || loanDetails.offer_url, '_blank')}
                                                >
                                                    View Signed Offer Letter
                                                </Button>
                                            )}
                                            {loanDetails.files?.account_statement_url && (
                                                <Button block type="dashed" href={loanDetails.files.account_statement_url} target="_blank">
                                                    View Bank Statement
                                                </Button>
                                            )}
                                        </Space>
                                    </Card>

                                    <Card title="Verification Documents" style={{ borderRadius: 16 }}>
                                        {loanDetails.verification_documents && Object.values(loanDetails.verification_documents).some(v => v) ? (
                                            <Space direction="vertical" style={{ width: '100%' }}>
                                                {Object.entries(loanDetails.verification_documents)
                                                    .filter(([key, value]) => {
                                                        const isGuarantor2Doc = key === 'guarantor_2_crc_report' || key === 'home_verification_video_guarantor_2';
                                                        if (isGuarantor2Doc) return value && guarantorCount > 1;
                                                        return value;
                                                    })
                                                    .map(([key, value]) => (
                                                        <div key={key} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                                                            <Text style={{ fontSize: 13 }}>{key.replace(/_/g, ' ')}</Text>
                                                            <Button
                                                                type="link"
                                                                size="small"
                                                                icon={<FileTextOutlined />}
                                                                href={value}
                                                                target="_blank"
                                                            >
                                                                View
                                                            </Button>
                                                        </div>
                                                    ))}
                                            </Space>
                                        ) : (
                                            <Text type="secondary" italic>No verification documents uploaded yet.</Text>
                                        )}
                                    </Card>

                                    {loanDetails.loan_admin_checklist && (
                                        <Card title="Admin Checklist" style={{ borderRadius: 16 }}>
                                            {Object.entries(loanDetails.loan_admin_checklist).map(([key, value]) => (
                                                <div key={key} style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                                                    <Text style={{ fontSize: 12 }}>{key.replace(/_/g, ' ')}</Text>
                                                    {value ? <Tag color="success">Verified</Tag> : <Tag color="default">Pending</Tag>}
                                                </div>
                                            ))}
                                        </Card>
                                    )}

                                    {loanDetails.booked_by_details && (
                                        <Card title={<Space><UserOutlined /> Booked By</Space>} style={{ borderRadius: 16 }}>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
                                                <Avatar size="small" icon={<UserOutlined />} />
                                                <div>
                                                    <Text strong block>{loanDetails.booked_by_details.first_name} {loanDetails.booked_by_details.last_name}</Text>
                                                    <Text type="secondary" style={{ fontSize: 12 }}>{loanDetails.booked_by_details.email}</Text>
                                                </div>
                                            </div>
                                            <InfoRow label="Booking Date" value={moment(loanDetails.created_at).format('LLL')} />
                                        </Card>
                                    )}
                                </Space>
                            </Col>
                        </Row>
                    </TabPane>
                    <TabPane tab="Repayment History" key="repayments">
                        <Card title={<Space><HistoryOutlined /> Loan Repayment History</Space>} style={{ borderRadius: 16 }}>
                            <div style={{ marginBottom: 20, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <Search
                                    placeholder="Filter by Transaction Reference"
                                    allowClear
                                    onSearch={value => setRepaymentFilter(value)}
                                    style={{ width: 400 }}
                                    enterButton
                                />
                                <Button
                                    type="primary"
                                    icon={<HistoryOutlined />}
                                    onClick={() => {
                                        setIsReconcileModalVisible(true);
                                        setReconcileResult(null);
                                        setReconcileRef('');
                                    }}
                                    style={{ borderRadius: 8 }}
                                >
                                    Manual Reconcile
                                </Button>
                            </div>
                            <Table
                                loading={fetchingRepayments}
                                dataSource={repayments.map((item, index) => ({ ...item, key: item.id || index }))}
                                columns={[
                                    {
                                        title: 'Payment Date',
                                        dataIndex: 'payment_date',
                                        key: 'payment_date',
                                        render: (date) => date ? moment(date).format('MMM Do YYYY') : 'N/A',
                                    },
                                    {
                                        title: 'Amount Due',
                                        dataIndex: 'amount_due',
                                        key: 'amount_due',
                                        render: (amount) => `₦${Number(amount || 0).toLocaleString()}`,
                                        align: 'right',
                                    },
                                    {
                                        title: 'Amount Paid',
                                        dataIndex: 'amount_paid',
                                        key: 'amount_paid',
                                        render: (amount) => `₦${Number(amount || 0).toLocaleString()}`,
                                        align: 'right',
                                    },
                                    {
                                        title: 'Status',
                                        dataIndex: 'status',
                                        key: 'status',
                                        render: (status) => (
                                            <Tag color={status === 'Completed' || status === 'Success' || status === 'Successful' ? 'success' : 'orange'}>
                                                {status?.toUpperCase() || 'PENDING'}
                                            </Tag>
                                        ),
                                    },
                                    {
                                        title: 'Transaction ID',
                                        dataIndex: 'transaction_id',
                                        key: 'transaction_id',
                                        render: (id) => <Text copyable>{id}</Text>
                                    },
                                    {
                                        title: 'Notes',
                                        dataIndex: 'notes',
                                        key: 'notes',
                                    },
                                    ...(isFinanceApprover ? [{
                                        title: 'Action',
                                        key: 'action',
                                        render: (_, record) => {
                                            const isReversible = record.status === 'successful' || record.status === 'Success' || record.status === 'Successful';
                                            return (
                                                <Button
                                                    type="link"
                                                    danger
                                                    disabled={!isReversible}
                                                    onClick={() => handleReverseRepayment(record.id)}
                                                    style={{ padding: 0 }}
                                                >
                                                    Reverse
                                                </Button>
                                            );
                                        }
                                    }] : [])
                                ]}
                                pagination={{ pageSize: 10 }}
                            />
                        </Card>
                    </TabPane>
                </Tabs>
            </div>

            <LoanHistoryModal showModal={isLoanHistoryModalVisible} hideModal={() => setIsLoanHistoryModalVisible(false)} loanId={id} />

            <Modal
                title="Guarantor Account Details"
                visible={isGuarantorDetailsModalVisible}
                onCancel={() => setIsGuarantorDetailsModalVisible(false)}
                footer={[
                    <Button key="close" onClick={() => setIsGuarantorDetailsModalVisible(false)}>Close</Button>
                ]}
                width={500}
            >
                {selectedGuarantorDetails && (
                    <Descriptions bordered column={1} size="small">
                        <Descriptions.Item label="Account Name">{selectedGuarantorDetails.accountName || selectedGuarantorDetails.fullName}</Descriptions.Item>
                        <Descriptions.Item label="Account Number">{selectedGuarantorDetails.accountNo || selectedGuarantorDetails.accountNumber}</Descriptions.Item>
                        <Descriptions.Item label="Balance">₦{selectedGuarantorDetails.balance?.toLocaleString()}</Descriptions.Item>
                        <Descriptions.Item label="Phone Number">{selectedGuarantorDetails.phoneNumber}</Descriptions.Item>
                        <Descriptions.Item label="Email">{selectedGuarantorDetails.email}</Descriptions.Item>
                        <Descriptions.Item label="Tier">
                            <Tag color="blue">{selectedGuarantorDetails.tierDisplayName || selectedGuarantorDetails.tierName || 'N/A'}</Tag>
                        </Descriptions.Item>
                        <Descriptions.Item label="Status">
                            <Tag color={selectedGuarantorDetails.status === 'ACTIVE' ? 'success' : 'error'}>{selectedGuarantorDetails.status}</Tag>
                        </Descriptions.Item>
                        <Descriptions.Item label="KYC Status">
                            <Tag color={selectedGuarantorDetails.kycStatus === 'VERIFIED' ? 'success' : 'warning'}>{selectedGuarantorDetails.kycStatus}</Tag>
                        </Descriptions.Item>
                    </Descriptions>
                )}
            </Modal>

            <ApprovalHistoryModal
                visible={isApprovalHistoryModalVisible}
                onCancel={() => setIsApprovalHistoryModalVisible(false)}
                historyData={approvalHistory}
            />

            {/* Profile Detail Modal */}
            <Modal
                title="Full User Profile"
                visible={isOtherDataModalVisible}
                onCancel={() => setIsOtherDataModalVisible(false)}
                footer={null}
                width={800}
            >
                <Descriptions bordered column={2}>
                    {Object.entries(loanDetails.user?.other_data || {})
                        .filter(([key, val]) => typeof val !== 'object')
                        .map(([key, value]) => (
                            <Descriptions.Item label={key} key={key}>{String(value)}</Descriptions.Item>
                        ))
                    }
                </Descriptions>
            </Modal>

            {/* Mandate Modal */}
            <Modal title="Mandate Details" visible={isMandateModalVisible} onCancel={() => setIsMandateModalVisible(false)} footer={null} width={800}>
                {mandateDetails && (
                    <Space direction="vertical" size="large" style={{ width: '100%' }}>
                        <Descriptions bordered column={2} title="General Information">
                            <Descriptions.Item label="Mandate ID">{mandateDetails.id}</Descriptions.Item>
                            <Descriptions.Item label="Reference">{mandateDetails.reference}</Descriptions.Item>
                            <Descriptions.Item label="Status">
                                <Tag color={mandateDetails.status === 'approved' ? 'green' : 'orange'}>
                                    {mandateDetails.status?.toUpperCase()}
                                </Tag>
                            </Descriptions.Item>
                            <Descriptions.Item label="Verification Method">{mandateDetails.verification_method?.replace(/_/g, ' ')}</Descriptions.Item>
                            <Descriptions.Item label="Amount">₦{(mandateDetails.amount / 100).toLocaleString()}</Descriptions.Item>
                            <Descriptions.Item label="Balance">₦{(mandateDetails.balance / 100).toLocaleString()}</Descriptions.Item>
                            <Descriptions.Item label="Start Date">{moment(mandateDetails.start_date).format('LL')}</Descriptions.Item>
                            <Descriptions.Item label="End Date">{moment(mandateDetails.end_date).format('LL')}</Descriptions.Item>
                        </Descriptions>

                        <Descriptions bordered column={2} title="Account Details">
                            <Descriptions.Item label="Account Name">{mandateDetails.account_name}</Descriptions.Item>
                            <Descriptions.Item label="Account Number">{mandateDetails.account_number}</Descriptions.Item>
                            <Descriptions.Item label="Bank">{mandateDetails.institution?.name}</Descriptions.Item>
                            <Descriptions.Item label="Bank Code">{mandateDetails.institution?.bank_code}</Descriptions.Item>
                            <Descriptions.Item label="NIP Code">{mandateDetails.institution?.nip_code}</Descriptions.Item>
                            <Descriptions.Item label="NIBSS Code">{mandateDetails.nibss_code}</Descriptions.Item>
                        </Descriptions>

                        <Descriptions bordered column={2} title="Configuration & Status">
                            <Descriptions.Item label="Mandate Type">{mandateDetails.mandate_type?.toUpperCase()}</Descriptions.Item>
                            <Descriptions.Item label="Debit Type">{mandateDetails.debit_type?.toUpperCase()}</Descriptions.Item>
                            <Descriptions.Item label="Approved">
                                {mandateDetails.approved ? <Tag color="success">YES</Tag> : <Tag color="error">NO</Tag>}
                            </Descriptions.Item>
                            <Descriptions.Item label="Ready to Debit">
                                {mandateDetails.ready_to_debit ? <Tag color="success">YES</Tag> : <Tag color="error">NO</Tag>}
                            </Descriptions.Item>
                            <Descriptions.Item label="Live Mode">
                                {mandateDetails.live_mode ? <Tag color="blue">LIVE</Tag> : <Tag color="default">TEST</Tag>}
                            </Descriptions.Item>
                            <Descriptions.Item label="Created Date">{moment(mandateDetails.date).format('LLL')}</Descriptions.Item>
                            <Descriptions.Item label="Narration" span={2}>{mandateDetails.narration}</Descriptions.Item>
                        </Descriptions>

                        {mandateDetails.linked_mandates?.length > 0 && (
                            <Card title="Linked Mandates" size="small">
                                <Table
                                    dataSource={mandateDetails.linked_mandates}
                                    pagination={false}
                                    size="small"
                                    columns={[
                                        { title: 'Account Name', dataIndex: 'account_name', key: 'account_name' },
                                        { title: 'Account Number', dataIndex: 'account_number', key: 'account_number' },
                                        { title: 'Bank', dataIndex: 'bank_name', key: 'bank_name' },
                                        {
                                            title: 'Ready',
                                            dataIndex: 'is_ready_to_debit',
                                            key: 'is_ready_to_debit',
                                            render: (ready) => ready ? <Tag color="success">YES</Tag> : <Tag color="error">NO</Tag>
                                        },
                                    ]}
                                />
                            </Card>
                        )}
                    </Space>
                )}
            </Modal>

            {/* OTP Verification Modal */}
            <Modal
                title="Verify Guarantor OTP"
                visible={isVerifyOtpModalVisible}
                onOk={handleVerifyGuarantorOtp}
                onCancel={() => {
                    setIsVerifyOtpModalVisible(false);
                    setOtpInput('');
                }}
                confirmLoading={verifyingOtp}
                okText="Verify OTP"
            >
                <p>Enter the 6-digit OTP sent to the guarantor.</p>
                <Input
                    placeholder="Enter OTP"
                    maxLength={6}
                    value={otpInput}
                    onChange={e => setOtpInput(e.target.value)}
                    autoFocus
                />
                <div style={{ marginTop: 12, textAlign: 'right' }}>
                    <Button
                        type="link"
                        size="small"
                        loading={resendingOtp}
                        onClick={() => handleResendGuarantorOtp(currentGuarantorAuthUserId)}
                    >
                        Resend OTP
                    </Button>
                </div>
            </Modal>
            {/* Manual Reconciliation Modal */}
            <Modal
                title="Manual Payment Reconciliation"
                visible={isReconcileModalVisible}
                onCancel={() => setIsReconcileModalVisible(false)}
                footer={[
                    <Button key="cancel" onClick={() => setIsReconcileModalVisible(false)}>
                        Cancel
                    </Button>,
                    <Button
                        key="submit"
                        type="primary"
                        loading={reconciling}
                        onClick={handleManualReconcile}
                    >
                        Reconcile
                    </Button>
                ]}
                width={600}
            >
                <div style={{ marginBottom: 20 }}>
                    <Text type="secondary" block style={{ marginBottom: 8 }}>Transaction Reference</Text>
                    <Input
                        placeholder="Enter Transaction Reference"
                        value={reconcileRef}
                        onChange={e => setReconcileRef(e.target.value)}
                    />
                </div>
                <div style={{ marginBottom: 20 }}>
                    <Checkbox
                        checked={isDryRun}
                        onChange={e => setIsDryRun(e.target.checked)}
                    >
                        Dry Run (Simulation only, no actual changes)
                    </Checkbox>
                </div>

                {reconcileResult && (
                    <div style={{ marginTop: 24 }}>
                        <Divider>Reconciliation Result</Divider>
                        <Alert
                            message={reconcileResult.message}
                            type={reconcileResult.status ? "success" : "error"}
                            showIcon
                            style={{ marginBottom: 16 }}
                        />
                        {reconcileResult.data && (
                            <Descriptions bordered column={1} size="small">
                                <Descriptions.Item label="Customer Name">{reconcileResult.data.customer_name}</Descriptions.Item>
                                <Descriptions.Item label="Installment Aligned">
                                    <Tag color={reconcileResult.data.installment_aligned ? "success" : "warning"}>
                                        {reconcileResult.data.installment_aligned ? "YES" : "NO"}
                                    </Tag>
                                </Descriptions.Item>
                                <Descriptions.Item label="Matched Installment Date">
                                    {reconcileResult.data.matched_installment_date ? moment(reconcileResult.data.matched_installment_date).format('LLL') : 'N/A'}
                                </Descriptions.Item>
                                <Descriptions.Item label="Loan ID">
                                    <Text copyable>{reconcileResult.data.loan_id}</Text>
                                </Descriptions.Item>
                            </Descriptions>
                        )}
                    </div>
                )}
            </Modal>

            <Modal
                title="Update Loan Repayment Cycle"
                visible={isUpdateCycleModalVisible}
                onOk={handleUpdateRepaymentCycle}
                onCancel={() => setIsUpdateCycleModalVisible(false)}
                confirmLoading={updatingCycle}
                okText="Update Cycle"
                width={500}
            >
                <Form layout="vertical">
                    <Row gutter={16}>
                        <Col span={12}>
                            <Form.Item label="Repayment Cycle Value" required>
                                <Input
                                    type="number"
                                    min={1}
                                    value={repaymentCycleValue}
                                    onChange={(e) => setRepaymentCycleValue(Number(e.target.value))}
                                />
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item label="Repayment Cycle Unit" required>
                                <Select
                                    value={repaymentCycleUnit}
                                    onChange={(val) => setRepaymentCycleUnit(val)}
                                    options={[
                                        { value: 'hours', label: 'Hours' },
                                        { value: 'days', label: 'Days' },
                                        { value: 'weeks', label: 'Weeks' },
                                        { value: 'months', label: 'Months' },
                                    ]}
                                />
                            </Form.Item>
                        </Col>
                    </Row>
                    <Row gutter={16}>
                        <Col span={12}>
                            <Form.Item label="First Repayment Delay Value">
                                <Input
                                    type="number"
                                    min={1}
                                    value={firstRepaymentDelayValue}
                                    onChange={(e) => setFirstRepaymentDelayValue(e.target.value ? Number(e.target.value) : '')}
                                    placeholder="Optional"
                                />
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item label="First Repayment Delay Unit">
                                <Select
                                    value={firstRepaymentDelayUnit}
                                    onChange={(val) => setFirstRepaymentDelayUnit(val)}
                                    placeholder="Optional"
                                    allowClear
                                    options={[
                                        { value: 'hours', label: 'Hours' },
                                        { value: 'days', label: 'Days' },
                                        { value: 'weeks', label: 'Weeks' },
                                        { value: 'months', label: 'Months' },
                                    ]}
                                />
                            </Form.Item>
                        </Col>
                    </Row>
                </Form>
            </Modal>
        </Layout>
    );
};

export default LoanViewPage;
